import React from 'react';

import cx from 'classnames';

import styles from './UCITable.scss';
import { simpleDate, simpleDateTime } from 'lane-shared/helpers/formatters';
import {
  NativeFilterTypes,
  Column,
  Sort,
  Table,
  PageSizeType,
  FilterType,
  Tooltip,
} from 'design-system-web';
import { useIsAdminView } from 'hooks';
import { routes } from 'lane-shared/config';
import { Link, useHistory } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { getTimeZoneFromInteraction } from 'lane-shared/hooks/useInteractionTimeZone';
import { UserContentInteraction } from 'graphql-query-contracts';
import { INTERACTION_STATES } from 'constants-interactions';
import { ChipSelect, ChipStyle } from 'components/ads';
import { InteractionWithCompanies } from 'react-hooks';

type Props = {
  sectionId: string;
  selectedSort: Sort['id'];
  selectedOrder: Sort['direction'];
  additionalHeaders?: Column<UserContentInteraction>[];
  className?: string;
  style?: React.CSSProperties;
  interactions: UserContentInteraction[];
  pageSize?: PageSizeType;
  onRefresh: () => void;
  exportAllResults: () => void;
  exportCurrentPageResults: () => void;
  exportFilteredResults: () => void;
  totalRows?: number;
  loading?: boolean;
  keyword?: string;
  showCompanyColumn?: boolean;
  companyFilterOptions?: Array<{ label: string; value: string }>;
};

export function UCITableV2({
  sectionId,
  className,
  style,
  interactions,
  selectedSort,
  selectedOrder,
  additionalHeaders,
  pageSize,
  onRefresh,
  exportAllResults,
  exportCurrentPageResults,
  exportFilteredResults,
  totalRows,
  loading = false,
  keyword,
  showCompanyColumn = false,
  companyFilterOptions = [],
}: Props) {
  const [isAdminView, channelSlug] = useIsAdminView();
  const { t } = useTranslation();
  const history = useHistory();

  function getInteractionLink(interaction: UserContentInteraction) {
    if (isAdminView && channelSlug) {
      // if we are on a channel admin page, use that slug and direct
      // to the team member page, that will have more info about this user.
      return routes.channelAdminInteraction
        .replace(':id', channelSlug)
        .replace(':interactionId', interaction._id);
    }

    // otherwise send them to the public user page.
    return routes.interaction.replace(':id', interaction._id);
  }

  function getUserLink(interaction: UserContentInteraction) {
    if (isAdminView && channelSlug) {
      // if we are on a channel admin page, use that slug and direct
      // to the team member page, that will have more info about this user.
      return routes.channelAdminTeamMember
        .replace(':id', channelSlug)
        .replace(':userId', interaction.user._id);
    }

    // otherwise send them to the public user page.
    return routes.user.replace(':id', interaction.user._id);
  }

  const interactionDateRenderer = (
    uci: UserContentInteraction,
    date: Date,
    includeTime = false
  ) => {
    const timeZone = getTimeZoneFromInteraction(uci);

    return includeTime
      ? simpleDateTime(date, timeZone)
      : simpleDate(date, timeZone);
  };

  /**
   * Displays count with tooltip if multiple companies, single name, or dash if none.
   * Uses totalCount for accurate count (edges are paginated to 100).
   */
  function renderCompanyCell(
    interaction: InteractionWithCompanies
  ): React.ReactNode {
    const totalCount = interaction.companiesConnection?.totalCount ?? 0;
    const allEdges = interaction.companiesConnection?.edges ?? [];
    const companies = allEdges
      .map(edge => edge?.node)
      .filter((node): node is NonNullable<typeof node> => !!node);

    if (totalCount === 0) {
      return <span>–</span>;
    }

    if (totalCount === 1 && companies.length > 0) {
      return <span>{companies[0].name}</span>;
    }

    return (
      <Tooltip
        placement="top"
        TooltipComponent={
          <span>{companies.map(node => node.name).join(', ')}</span>
        }
      >
        <ChipSelect.NonInteractive
          key={interaction._id}
          value={t('web.admin.channel.users.all.table.companiesAssigned', {
            count: totalCount,
          })}
          type={ChipStyle.Grey}
          withStatusIcon={false}
          doTranslate={false}
        />
      </Tooltip>
    );
  }

  const baseTableColumns: Column<UserContentInteraction>[] = [
    {
      key: '_created',
      header: 'Created',
      renderCell: (_: string, uci: UserContentInteraction) =>
        interactionDateRenderer(uci, uci._created),
    },
    {
      key: '_updated',
      header: 'Updated',
      renderCell: (_: string, uci: UserContentInteraction) =>
        interactionDateRenderer(uci, uci._updated),
    },
    {
      key: 'status',
      header: 'Status',
    },
    {
      key: 'contentData.name',
      header: 'Content',
      renderCell: (contentName, interaction) => (
        <Link to={routes.content.replace(':id', interaction.contentData._id)}>
          {contentName}
        </Link>
      ),
      disableSorting: true,
    },
    {
      key: 'user.profile.name',
      header: 'User',
      renderCell: (profileName, interaction) => (
        <Link to={getUserLink(interaction)}>{profileName}</Link>
      ),
      disableSorting: true,
    },
    ...(showCompanyColumn
      ? [
          {
            key: 'companies',
            header: t(
              'web.admin.section.interaction.table.columns.companies'
            ),
            renderCell: (
              _: unknown,
              interaction: UserContentInteraction
            ) => renderCompanyCell(interaction as InteractionWithCompanies),
            disableSorting: true,
          } as Column<UserContentInteraction>,
        ]
      : []),
    {
      key: 'startDate',
      header: 'Start',
      renderCell: (_: string, uci: UserContentInteraction) =>
        interactionDateRenderer(uci, uci.startDate, true),
    },
    {
      key: 'endDate',
      header: 'End',
      renderCell: (_: string, uci: UserContentInteraction) =>
        interactionDateRenderer(uci, uci.endDate, true),
    },
  ];

  const rowActions = [
    {
      label: 'Edit',
      onClick: (uci: UserContentInteraction) => {
        history.push(getInteractionLink(uci));
      },
    },
  ];

  const exportOptions = [
    {
      label: t('web.admin.content.interaction.table.exportOptions.filtered'),
      onClick: exportFilteredResults,
    },
    {
      label: t('web.admin.content.interaction.table.exportOptions.page'),
      onClick: exportCurrentPageResults,
    },
    {
      label: t('web.admin.content.interaction.table.exportOptions.all'),
      onClick: exportAllResults,
    },
  ];

  const allTableColumns = baseTableColumns.concat(additionalHeaders || []);

  const filters: FilterType[] = [
    {
      key: 'interactionCreationDates',
      type: NativeFilterTypes.DateRange,
      label: t(
        'web.admin.section.interaction.table.filters.createdDateRange.label'
      ),
      isPromoted: true,
    },
    {
      key: 'dateRangeFilter',
      type: NativeFilterTypes.DateRange,
      label: t(
        'web.admin.section.interaction.table.filters.startEndDateRange.label'
      ),
      isPromoted: true,
    },
    {
      key: 'status',
      type: NativeFilterTypes.Select,
      label: t('Status'),
      isPromoted: true,
      options: INTERACTION_STATES.map(state => ({
        label: state,
        value: state,
      })),
    },
    ...(showCompanyColumn && companyFilterOptions.length > 0
      ? [
          {
            key: 'companyId',
            type: NativeFilterTypes.Select as const,
            label: t(
              'web.admin.section.interaction.table.filters.company.label'
            ),
            isPromoted: true,
            options: companyFilterOptions,
          },
        ]
      : []),
  ];

  return (
    <div className={cx(styles.UCITable, className)} style={style}>
      <Table
        isLoading={loading}
        tableKey={`${sectionId}-uci-table`}
        columns={allTableColumns}
        data={interactions}
        sorting={{
          id: selectedSort,
          direction: selectedOrder,
        }}
        defaultPageSize={pageSize}
        pagination="server"
        showColumnVisibility
        onRefreshClick={onRefresh}
        exportOptions={exportOptions}
        totalRows={totalRows}
        rowActions={rowActions}
        getRowId={(row: UserContentInteraction) => row._id}
        showTotalItemsCount
        hasKeywordFilter
        keywordFilter={keyword || ''}
        queryStringsEnabled
        filters={filters}
      />
    </div>
  );
}
